import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactPlayer from 'react-player'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  downloadCertificate,
  getStoredCertificateName,
  prepareCertificateDownload,
  setStoredCertificateName,
  resolveCertificateSignatureDate,
  CertificateMeta,
} from '../../../utils/helpers'
import { getPurchasedCourseDetail } from '../../../api/user'
import { markLessonAsWatched, getAllCBTAttempt } from '../../../api/courses'
import { all_routes } from '../../router/all_routes'

const CourseContent = () => {
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [completedLessons, setCompletedLessons] = useState<number[]>([])
  const [certificateName, setCertificateName] = useState('')
  const [storedCertificateName, setStoredCertificateNameState] = useState('')
  const [selectedLesson, setSelectedLesson] = useState<any>(null)
  const [isFinishingCourse, setIsFinishingCourse] = useState(false)
  const [finishProgress, setFinishProgress] = useState({ current: 0, total: 0 })
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  // Fetch course data using the new API
  const { data: courseResponse, isLoading, isError, error } = useQuery({
    queryKey: ['purchasedCourseDetail', id],
    queryFn: () => getPurchasedCourseDetail(id!),
    enabled: !!id,
  })

  const course = courseResponse?.data
  const hasPosttest =
    course?.has_questions === 'yes' ||
    course?.has_questions === true ||
    Number(course?.questions_count || 0) > 0
  const attemptCount = Number(
    course?.user_attempt_count ?? course?.attempt_count ?? 0,
  )
  const rawMax = Number(course?.max_attempt)
  const maxAttempt = rawMax > 0 ? rawMax : 1
  const remainingAttempts = Math.max(maxAttempt - attemptCount, 0)
  const canRetry = attemptCount < maxAttempt
  const hasAttempt = attemptCount > 0
  const latestAttemptId = course?.latest_attempt_id
  const latestScore =
    course?.latest_score !== null && course?.latest_score !== undefined
      ? course.latest_score
      : null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isPassed = course?.is_passed === true

  const [bestCbtScore, setBestCbtScore] = useState<number | null>(null)
  const [showGroupModal, setShowGroupModal] = useState<boolean>(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
  const [hasClickedFeedback, setHasClickedFeedback] = useState<boolean>(() => {
    if (!id) return false
    return localStorage.getItem(`feedback_clicked_${id}`) === 'true'
  })

  const getBestCbtScore = async (): Promise<number | null> => {
    if (!course?.id) return latestScore

    try {
      const res = await getAllCBTAttempt(String(course.id))
      const attempts = Array.isArray(res?.data) ? res.data : []

      const bestScore = attempts.reduce((best: number, attempt: any) => {
        const score = Number(attempt?.score)
        if (Number.isFinite(score)) {
          return Math.max(best, score)
        }
        return best
      }, Number.NEGATIVE_INFINITY)

      return bestScore === Number.NEGATIVE_INFINITY ? null : bestScore
    } catch (error) {
      console.error('Failed to fetch CBT attempts for best score:', error)
      return latestScore
    }
  }

  const savedCertificateName =
    course?.certificate_downloads?.name || storedCertificateName
  const totalLessons =
    course?.topics?.reduce(
      (acc: number, topic: any) => acc + (topic.lessons?.length || 0),
      0,
    ) || 0
  const watchedLessons =
    course?.topics?.reduce(
      (acc: number, topic: any) =>
        acc + (topic.lessons?.filter((lesson: any) => lesson.is_watched).length || 0),
      0,
    ) || 0

  const isDateFinished = course?.end_date
    ? new Date().getTime() >= new Date(course.end_date).getTime()
    : true

  const hasFeedbackLink = Boolean(
    course?.link_feedback &&
      String(course.link_feedback).trim() !== '' &&
      String(course.link_feedback).trim() !== 'null' &&
      String(course.link_feedback).trim() !== 'undefined',
  )

  const isFeedbackTimeLocked = Boolean(
    course?.end_date && new Date().getTime() < new Date(course.end_date).getTime(),
  )

  const isFeedbackRequiredAndPending = hasFeedbackLink && !hasClickedFeedback

  const isCbtRequiredAndPending = hasPosttest && !hasAttempt

  const isCertificateLocked =
    !isDateFinished || isFeedbackRequiredAndPending || isCbtRequiredAndPending

  const canStartQuiz = hasPosttest && (remainingAttempts ?? 0) > 0 && !hasAttempt
  const canRetryQuiz = hasPosttest && (remainingAttempts ?? 0) > 0 && hasAttempt && canRetry

  const handleFeedbackClick = () => {
    if (!hasFeedbackLink || isFeedbackTimeLocked) return

    if (id) {
      localStorage.setItem(`feedback_clicked_${id}`, 'true')
    }
    setHasClickedFeedback(true)
    window.open(course.link_feedback, '_blank')
  }

  const handleCertificateDownloadClick = () => {
    if (isFeedbackRequiredAndPending) {
      setShowFeedbackModal(true)
      return
    }
    handleCertificateDownload()
  }

  // Mutation for marking lesson as watched
  const markLessonMutation = useMutation({
    mutationFn: markLessonAsWatched,
    onSuccess: () => {
      // Invalidate and refetch course data to get updated progress and is_watched status
      queryClient.invalidateQueries({ queryKey: ['purchasedCourseDetail', id] })
    },
    onError: (error) => {
      console.error('Error marking lesson as watched:', error)
      // Revert the local state change on error
      setCompletedLessons((prev) =>
        prev.filter((lessonId) => lessonId !== markLessonMutation.variables),
      )
    },
  })

  // Set default video URL from first lesson if available
  const videoUrl =
    selectedLesson?.video_link ||
    course?.topics?.[0]?.lessons?.[0]?.video_link ||
    ''

  // Load best score when CBT is enabled
  useEffect(() => {
    if (!hasPosttest) return
    if (!hasAttempt) {
      setBestCbtScore(null)
      return
    }

    let isActive = true

    const loadBestScore = async () => {
      const score = await getBestCbtScore()
      if (isActive) {
        setBestCbtScore(score)
      }
    }

    loadBestScore()

    return () => {
      isActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPosttest, hasAttempt, course?.id, latestScore])

  // Set first lesson as default when course data is loaded
  useEffect(() => {
    if (course?.topics?.[0]?.lessons?.[0] && !selectedLesson) {
      setSelectedLesson(course.topics[0].lessons[0])
    }
  }, [course, selectedLesson])

  // Initialize completedLessons with lessons that have is_watched: true
  // This ensures that lessons marked as watched on the server are automatically checked
  useEffect(() => {
    if (course?.topics) {
      const watchedLessonIds: number[] = []
      course.topics.forEach((topic) => {
        topic.lessons?.forEach((lesson) => {
          if (lesson.is_watched) {
            watchedLessonIds.push(lesson.id)
          }
        })
      })
      setCompletedLessons(watchedLessonIds)
    }
  }, [course])

  useEffect(() => {
    setStoredCertificateNameState(getStoredCertificateName(course?.id))
  }, [course?.id])

  useEffect(() => {
    if (!savedCertificateName || !course?.id) return
    if (
      course.certificate_downloads?.certificate_number ||
      course.certificate_downloads?.qr_url
    ) {
      return
    }

    const score =
      bestCbtScore !== null
        ? String(bestCbtScore)
        : latestScore !== null
        ? String(latestScore)
        : ''

    prepareCertificateDownload(
      Number(course.id),
      savedCertificateName,
      hasPosttest ? score : undefined,
      hasPosttest ? (course?.min_passing_score || '') : undefined,
    )
  }, [
    course?.id,
    course?.certificate_downloads?.name,
    course?.certificate_downloads?.certificate_number,
    course?.certificate_downloads?.qr_url,
    savedCertificateName,
    hasPosttest,
    bestCbtScore,
    latestScore,
    course?.min_passing_score,
  ])

  const handleLessonSelect = (lesson: any) => {
    setSelectedLesson(lesson)
  }

  const handleCertificateDownload = async () => {
    if (isCertificateLocked) {
      alert(
        hasPosttest
          ? 'Sertifikat belum tersedia. Selesaikan Ujian CBT terlebih dahulu.'
          : 'Sertifikat belum tersedia. Selesaikan seluruh pembelajaran terlebih dahulu.',
      )
      return
    }

    const bestScore = await getBestCbtScore()
    const scoreText =
      bestScore !== null
        ? String(bestScore)
        : latestScore !== null
        ? String(latestScore)
        : ''

    const signatureDate = resolveCertificateSignatureDate(
      course?.certificate_event_dates || undefined,
      course?.certificate_signature_date || course?.end_date || undefined,
    )

    const certificateMeta: CertificateMeta = {
      eventDates: course?.certificate_event_dates || undefined,
      jplTotal: course?.certificate_jpl_total || undefined,
      jplNote: course?.certificate_jpl_note || undefined,
      speakers: course?.certificate_speakers || undefined,
      materials: course?.certificate_materials || undefined,
      materialTopicOnly: !hasPosttest,
      location: course?.certificate_location || undefined,
      signatureDate: signatureDate || undefined,
      certificateNumber:
        course?.certificate_downloads?.certificate_number || undefined,
      qrUrl: course?.certificate_downloads?.qr_url || undefined,
    }

    // Check if certificate_downloads exists and has a name
    if (savedCertificateName) {
      // Direct download with existing name
      const courseTopics =
        course?.studied?.map((topic) => ({ name: topic })) || []
      await downloadCertificate(
        savedCertificateName || course?.title || 'Course Certificate',
        undefined, // customFileName
        undefined, // pdfUrl
        savedCertificateName, // userName
        course?.title, // courseTitle
        true, // useAutoGeneration
        course?.id, // courseId
        courseTopics, // courseTopics
        hasPosttest,
        scoreText,
        course?.min_passing_score || '',
        certificateMeta,
      )
    } else {
      // Show modal for first time download (certificate_downloads is null)
      setShowCertificateModal(true)
    }
  }

  const confirmCertificateDownload = async () => {
    setShowCertificateModal(false)
    const name = certificateName || course?.title || 'Course Certificate'
    setStoredCertificateName(course?.id, name)
    setStoredCertificateNameState(name)

    const bestScore = await getBestCbtScore()
    const scoreText =
      bestScore !== null
        ? String(bestScore)
        : latestScore !== null
        ? String(latestScore)
        : ''

    const signatureDate = resolveCertificateSignatureDate(
      course?.certificate_event_dates || undefined,
      course?.certificate_signature_date || course?.end_date || undefined,
    )

    // Extract topics data for certificate
    const courseTopics =
      course?.studied?.map((topic) => ({ name: topic })) || []
    const certificateMeta: CertificateMeta = {
      eventDates: course?.certificate_event_dates || undefined,
      jplTotal: course?.certificate_jpl_total || undefined,
      jplNote: course?.certificate_jpl_note || undefined,
      speakers: course?.certificate_speakers || undefined,
      materials: course?.certificate_materials || undefined,
      materialTopicOnly: !hasPosttest,
      location: course?.certificate_location || undefined,
      signatureDate: signatureDate || undefined,
      certificateNumber:
        course?.certificate_downloads?.certificate_number || undefined,
      qrUrl: course?.certificate_downloads?.qr_url || undefined,
    }
    await downloadCertificate(
      name,
      undefined, // customFileName
      undefined, // pdfUrl
      name, // userName
      course?.title, // courseTitle
      true, // useAutoGeneration
      course?.id, // courseId
      courseTopics, // courseTopics
      hasPosttest,
      scoreText,
      course?.min_passing_score || '',
      certificateMeta,
    )
  }

  const handleLessonComplete = (lessonId: number) => {
    const isCurrentlyCompleted = completedLessons.includes(lessonId)

    // Update local state immediately for better UX
    setCompletedLessons((prev) =>
      isCurrentlyCompleted
        ? prev.filter((id) => id !== lessonId)
        : [...prev, lessonId],
    )

    // Only call API when marking as completed (not when unchecking)
    if (!isCurrentlyCompleted) {
      markLessonMutation.mutate(lessonId)
    }
  }

  const calculateProgress = () => {
    if (!course?.topics) return 0
    const totalLessons = course.topics.reduce(
      (acc: number, topic: any) => acc + (topic.lessons?.length || 0),
      0,
    )
    return totalLessons > 0
      ? Math.round((completedLessons.length / totalLessons) * 100)
      : 0
  }

  const handleJoinGroup = () => {
    if (course?.link_group && course?.link_group_loker) {
      setShowGroupModal(true)
    } else if (course?.link_group) {
      window.open(course.link_group, '_blank')
    } else if (course?.link_group_loker) {
      window.open(course.link_group_loker, '_blank')
    }
  }

  const handleDownloadMaterial = () => {
    if (course?.link_ebook) {
      window.open(course.link_ebook, '_blank')
    }
  }

  // Get all unwatched lessons
  const getUnwatchedLessons = () => {
    if (!course?.topics) return []
    const unwatchedLessons: number[] = []
    course.topics.forEach((topic) => {
      topic.lessons?.forEach((lesson) => {
        if (!lesson.is_watched && !completedLessons.includes(lesson.id)) {
          unwatchedLessons.push(lesson.id)
        }
      })
    })
    return unwatchedLessons
  }

  // Get unwatched lessons count for display
  const unwatchedCount = getUnwatchedLessons().length

  // Check if course is completed
  const isCourseCompleted = () => {
    if (!course?.topics) return false
    const totalLessons = course.topics.reduce(
      (acc: number, topic: any) => acc + (topic.lessons?.length || 0),
      0,
    )
    return completedLessons.length >= totalLessons
  }

  // Finish course by marking all unwatched lessons as watched
  const handleFinishCourse = async () => {
    const unwatchedLessons = getUnwatchedLessons()
    if (unwatchedLessons.length === 0) return

    setIsFinishingCourse(true)
    setFinishProgress({ current: 0, total: unwatchedLessons.length })

    try {
      // Process lessons in batches to avoid overwhelming the server
      const batchSize = 3
      for (let i = 0; i < unwatchedLessons.length; i += batchSize) {
        const batch = unwatchedLessons.slice(i, i + batchSize)

        // Process batch concurrently
        const promises = batch.map(async (lessonId) => {
          try {
            await markLessonAsWatched(lessonId)
            // Update local state immediately for better UX
            setCompletedLessons((prev) => [...prev, lessonId])
            setFinishProgress((prev) => ({
              ...prev,
              current: prev.current + 1,
            }))
          } catch (error) {
            console.error(`Error marking lesson ${lessonId} as watched:`, error)
          }
        })

        await Promise.all(promises)

        // Small delay between batches to be gentle on the server
        if (i + batchSize < unwatchedLessons.length) {
          await new Promise((resolve) => setTimeout(resolve, 500))
        }
      }

      // Refresh course data to get updated progress
      queryClient.invalidateQueries({ queryKey: ['purchasedCourseDetail', id] })
    } catch (error) {
      console.error('Error finishing course:', error)
    } finally {
      setIsFinishingCourse(false)
      setFinishProgress({ current: 0, total: 0 })
    }
  }

  // Helpers to navigate between lessons across topics
  const findCurrentPosition = () => {
    if (!course?.topics || !selectedLesson) return null
    for (let topicIndex = 0; topicIndex < course.topics.length; topicIndex++) {
      const lessons = course.topics[topicIndex]?.lessons || []
      for (let lessonIndex = 0; lessonIndex < lessons.length; lessonIndex++) {
        if (lessons[lessonIndex]?.id === selectedLesson.id) {
          return { topicIndex, lessonIndex }
        }
      }
    }
    return null
  }

  const getPrevLesson = () => {
    if (!course?.topics || !selectedLesson) return null
    const pos = findCurrentPosition()
    if (!pos) return null
    const { topicIndex, lessonIndex } = pos
    if (lessonIndex > 0) {
      return course.topics[topicIndex].lessons[lessonIndex - 1]
    }
    if (topicIndex > 0) {
      const prevTopicLessons = course.topics[topicIndex - 1]?.lessons || []
      if (prevTopicLessons.length > 0) {
        return prevTopicLessons[prevTopicLessons.length - 1]
      }
    }
    return null
  }

  const getNextLesson = () => {
    if (!course?.topics || !selectedLesson) return null
    const pos = findCurrentPosition()
    if (!pos) return null
    const { topicIndex, lessonIndex } = pos
    const currentTopicLessons = course.topics[topicIndex]?.lessons || []
    if (lessonIndex + 1 < currentTopicLessons.length) {
      return currentTopicLessons[lessonIndex + 1]
    }
    if (topicIndex + 1 < course.topics.length) {
      const nextTopicLessons = course.topics[topicIndex + 1]?.lessons || []
      if (nextTopicLessons.length > 0) {
        return nextTopicLessons[0]
      }
    }
    return null
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading course content...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-5 text-danger">
        <p>Error loading course: {error?.message || 'Unknown error'}</p>
        <p>Please try again later.</p>
      </div>
    )
  }

  if (!course) {
    return <div>Course not found.</div>
  }

  return (
    <>
      {/* Course Content */}
      <div className="content pt-0">
        <div className="container">
          <div className="course-watch-section">
            <div className="row g-4">
              <div className="col-lg-4 col-12 mb-4 mb-lg-0 border-0">
                <div
                  className="progress-overview-section"
                  style={{ padding: '0px' }}
                >
                  <div className="mb-4"></div>
                  <h3>{course.title}</h3>
                  <div className="mb-4">
                    <p className="mb-1">{calculateProgress()}% Complete</p>
                    <div
                      className="progress progress-xs mb-2"
                      role="progressbar"
                      aria-valuenow={calculateProgress()}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${calculateProgress()}%` }}
                      />
                    </div>
                    <span className="fw-medium">
                      Last activity on{' '}
                      {new Date().toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="mb-4">
                    <div className="d-grid gap-2" style={{ gap: '8px' }}>
                      {course.link_group && (
                        <button
                          type="button"
                          className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                          style={{
                            backgroundColor: '#00bcd4',
                            borderRadius: '50px',
                            padding: '8px 16px',
                            minHeight: '38px',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            letterSpacing: '0.1px',
                          }}
                          onClick={() => window.open(course.link_group, '_blank')}
                        >
                          <i className="isax isax-people me-2 fs-16" />
                          <span>Join Group Diskusi</span>
                        </button>
                      )}

                      {Boolean(course.link_group_loker && String(course.link_group_loker).trim() !== '' && String(course.link_group_loker).trim() !== 'null' && String(course.link_group_loker).trim() !== 'undefined') && (
                        <button
                          type="button"
                          className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                          style={{
                            backgroundColor: '#2ec4b6',
                            borderRadius: '50px',
                            padding: '8px 16px',
                            minHeight: '38px',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            letterSpacing: '0.1px',
                          }}
                          onClick={() => window.open(course.link_group_loker, '_blank')}
                        >
                          <i className="isax isax-briefcase me-2 fs-16" />
                          <span>Join Group Lowongan Kerja</span>
                        </button>
                      )}

                      {course.link_record && (
                        <button
                          type="button"
                          className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                          style={{
                            backgroundColor: '#7209b7',
                            borderRadius: '50px',
                            padding: '8px 16px',
                            minHeight: '38px',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            letterSpacing: '0.1px',
                          }}
                          onClick={() => window.open(course.link_record, '_blank')}
                        >
                          <i className="isax isax-video-play me-2 fs-16" />
                          <span>Akses Rekaman</span>
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center flex-wrap border-0 w-100"
                        disabled={isCertificateLocked}
                        style={{
                          backgroundColor: isCertificateLocked ? '#94a3b8' : '#f59e0b',
                          borderRadius: '50px',
                          padding: '8px 16px',
                          minHeight: '38px',
                          fontSize: '13.5px',
                          fontWeight: 600,
                          letterSpacing: '0.1px',
                          cursor: isCertificateLocked ? 'not-allowed' : 'pointer',
                          opacity: isCertificateLocked ? 0.85 : 1,
                        }}
                        onClick={handleCertificateDownloadClick}
                      >
                        <i className={`isax ${isCertificateLocked ? 'isax-lock' : 'isax-award'} me-2 fs-16`} />
                        <span>Download Sertifikat</span>
                        {isFeedbackRequiredAndPending && (
                          <span className="badge bg-danger ms-2 shadow-sm" style={{ fontSize: '10.5px', padding: '3px 7px', borderRadius: '20px' }}>
                            Isi Feedback Dulu
                          </span>
                        )}
                      </button>

                      {course.link_ebook && (
                        <button
                          type="button"
                          className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                          style={{
                            backgroundColor: '#6c5ce7',
                            borderRadius: '50px',
                            padding: '8px 16px',
                            minHeight: '38px',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            letterSpacing: '0.1px',
                          }}
                          onClick={handleDownloadMaterial}
                        >
                          <i className="isax isax-document-download me-2 fs-16" />
                          <span>Download Materi</span>
                        </button>
                      )}

                      {hasFeedbackLink && (
                        <button
                          type="button"
                          className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                          style={{
                            backgroundColor: isFeedbackTimeLocked ? '#94a3b8' : '#4361ee',
                            borderRadius: '50px',
                            padding: '8px 16px',
                            minHeight: '38px',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            letterSpacing: '0.1px',
                            cursor: isFeedbackTimeLocked ? 'not-allowed' : 'pointer',
                            opacity: isFeedbackTimeLocked ? 0.85 : 1,
                          }}
                          disabled={isFeedbackTimeLocked}
                          onClick={handleFeedbackClick}
                        >
                          <i className="isax isax-message-edit me-2 fs-16" />
                          <span>Feedback Peserta</span>
                          {hasClickedFeedback && (
                            <i className="isax isax-tick-circle text-white ms-2" />
                          )}
                        </button>
                      )}

                      {hasPosttest && (
                        <>
                          {maxAttempt > 0 && remainingAttempts !== null && (
                            <div
                              className="alert alert-info d-flex align-items-center gap-2 mb-0 py-2 px-3"
                              style={{
                                backgroundColor: '#e0f7fa',
                                borderColor: '#b2ebf2',
                                color: '#006064',
                                borderRadius: '10px',
                              }}
                            >
                              <i
                                className="isax isax-timer-start fs-18"
                                style={{ color: '#006064' }}
                              />
                              <span
                                className="fw-medium"
                                style={{ color: '#006064', fontSize: '13px' }}
                              >
                                Sisa percobaan kuis: {remainingAttempts} dari {maxAttempt}
                              </span>
                            </div>
                          )}

                          {canStartQuiz && (
                            <Link
                              to={all_routes.studentCBTInstruction.replace(
                                ':id',
                                course.id.toString(),
                              )}
                              className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                              style={{
                                backgroundColor: '#6366f1',
                                borderRadius: '50px',
                                padding: '8px 16px',
                                minHeight: '38px',
                                fontSize: '13.5px',
                                fontWeight: 600,
                                letterSpacing: '0.1px',
                                textDecoration: 'none',
                              }}
                            >
                              <i className="isax isax-play me-2 fs-16" />
                              <span>Mulai Ujian</span>
                            </Link>
                          )}
                          {canRetryQuiz && (
                            <Link
                              to={all_routes.studentCBTInstruction.replace(
                                ':id',
                                course.id.toString(),
                              )}
                              className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                              style={{
                                backgroundColor: '#f59e0b',
                                borderRadius: '50px',
                                padding: '8px 16px',
                                minHeight: '38px',
                                fontSize: '13.5px',
                                fontWeight: 600,
                                letterSpacing: '0.1px',
                                textDecoration: 'none',
                              }}
                            >
                              <i className="isax isax-refresh me-2 fs-16" />
                              <span>Ulangi Ujian</span>
                            </Link>
                          )}
                          {latestAttemptId ? (
                            <Link
                              to={all_routes.studentCBTReview.replace(
                                ':id',
                                String(latestAttemptId),
                              )}
                              className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                              style={{
                                backgroundColor: '#64748b',
                                borderRadius: '50px',
                                padding: '8px 16px',
                                minHeight: '38px',
                                fontSize: '13.5px',
                                fontWeight: 600,
                                letterSpacing: '0.1px',
                                textDecoration: 'none',
                              }}
                            >
                              <i className="isax isax-eye me-2 fs-16" />
                              <span>Lihat Pembahasan</span>
                            </Link>
                          ) : (
                            <span
                              className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100 disabled"
                              style={{
                                backgroundColor: '#94a3b8',
                                borderRadius: '50px',
                                padding: '8px 16px',
                                minHeight: '38px',
                                fontSize: '13.5px',
                                fontWeight: 600,
                                opacity: 0.6,
                              }}
                            >
                              <i className="isax isax-eye me-2 fs-16" />
                              <span>Lihat Pembahasan</span>
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Cara Mendapatkan Sertifikat Guide Card */}
                  <div
                    className="card border-0 shadow-sm mb-4"
                    style={{
                      borderRadius: '16px',
                      backgroundColor: '#f8fafc',
                      borderLeft: '4px solid #3b82f6',
                    }}
                  >
                    <div className="card-body p-3 p-sm-4">
                      <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#1e293b' }}>
                        <i className="isax isax-info-circle me-2 text-primary fs-18" />
                        Cara Mendapatkan Sertifikat
                      </h6>
                      <div className="d-flex flex-column gap-2.5">
                        <div className="d-flex align-items-start mb-2">
                          <span
                            className="badge rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{ width: '22px', height: '22px', fontSize: '11px' }}
                          >
                            1
                          </span>
                          <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500, lineHeight: '1.4' }}>
                            Pastikan tanggal dan waktu kelas telah selesai
                          </span>
                        </div>
                        <div className="d-flex align-items-start mb-2">
                          <span
                            className="badge rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{ width: '22px', height: '22px', fontSize: '11px' }}
                          >
                            2
                          </span>
                          <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500, lineHeight: '1.4' }}>
                            Klik Feedback Peserta, dan Isi Feedbacknya
                          </span>
                        </div>
                        <div className="d-flex align-items-start mb-2">
                          <span
                            className="badge rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{ width: '22px', height: '22px', fontSize: '11px' }}
                          >
                            3
                          </span>
                          <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500, lineHeight: '1.4' }}>
                            Klik Download sertifikat
                          </span>
                        </div>
                        <div className="d-flex align-items-start">
                          <span
                            className="badge rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{ width: '22px', height: '22px', fontSize: '11px' }}
                          >
                            4
                          </span>
                          <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500, lineHeight: '1.4' }}>
                            Input Nama dan Gelar, pastikan data sesuai generate sertifikat hanya bisa 1x
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course Topics */}
                  <div
                    className="accordions-items-seperate"
                    id="accordionSpacingExample"
                  >
                    {course.topics?.map((topic: any, topicIndex: number) => (
                      <div className="accordion-item" key={topic.id}>
                        <div
                          className="accordion-header"
                          id={`headingSpacing${topicIndex}`}
                        >
                          <div
                            className="accordion-button collapsed"
                            role="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#Spacing${topicIndex}`}
                            aria-expanded="false"
                            aria-controls={`Spacing${topicIndex}`}
                          >
                            <div>
                              <span className="d-block mb-1">
                                Topic {topicIndex + 1}
                              </span>
                              <h6 className="mb-0">{topic.name}</h6>
                            </div>
                          </div>
                        </div>
                        <div
                          id={`Spacing${topicIndex}`}
                          className="accordion-collapse collapse"
                          aria-labelledby={`headingSpacing${topicIndex}`}
                          data-bs-parent="#accordionSpacingExample"
                        >
                          <div className="accordion-body">
                            {topic.lessons?.map(
                              (lesson: any, lessonIndex: number) => (
                                <div
                                  className={`d-flex align-items-center justify-content-between mb-3 lesson-item ${
                                    selectedLesson?.id === lesson.id
                                      ? 'selected'
                                      : ''
                                  }`}
                                  key={lesson.id}
                                  onClick={() => handleLessonSelect(lesson)}
                                  style={{
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    backgroundColor:
                                      selectedLesson?.id === lesson.id
                                        ? '#f8f9fa'
                                        : 'transparent',
                                    border:
                                      selectedLesson?.id === lesson.id
                                        ? '1px solid #dee2e6'
                                        : '1px solid transparent',
                                  }}
                                >
                                  <div className="d-flex align-items-center">
                                    <span className="d-flex">
                                      <i
                                        className={`isax ${
                                          selectedLesson?.id === lesson.id
                                            ? 'isax-play-circle5 text-primary'
                                            : 'isax-play-circle5 text-success'
                                        } fs-24 me-1`}
                                      />
                                    </span>
                                    <p className="accordian-content mb-0">
                                      {lesson.name}
                                    </p>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <div className="form-check d-flex align-items-center">
                                      <input
                                        className="form-check-input me-2"
                                        type="checkbox"
                                        checked={completedLessons.includes(
                                          lesson.id,
                                        )}
                                        disabled={markLessonMutation.isPending}
                                        onChange={(e) => {
                                          e.stopPropagation()
                                          handleLessonComplete(lesson.id)
                                        }}
                                      />
                                      <span
                                        className={`d-inline-flex align-items-center ${
                                          completedLessons.includes(lesson.id)
                                            ? 'text-success'
                                            : ''
                                        }`}
                                      >
                                        {markLessonMutation.isPending &&
                                        markLessonMutation.variables ===
                                          lesson.id ? (
                                          <div
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                          >
                                            <span className="visually-hidden">
                                              Loading...
                                            </span>
                                          </div>
                                        ) : (
                                          <i
                                            className={`isax me-1 ${
                                              completedLessons.includes(
                                                lesson.id,
                                              )
                                                ? 'isax-tick-circle'
                                                : 'isax-circle'
                                            }`}
                                          />
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Finish Course Button */}
                  {!isCourseCompleted() && unwatchedCount > 0 && (
                    <div className="mt-4 p-3 border rounded bg-light">
                      <div className="text-center">
                        <h6 className="mb-2">Complete All Remaining Lessons</h6>
                        <p className="text-muted small mb-3">
                          Mark all {unwatchedCount} remaining lessons as watched
                        </p>
                        <button
                          className="btn btn-success btn-lg"
                          onClick={handleFinishCourse}
                          disabled={isFinishingCourse}
                        >
                          {isFinishingCourse ? (
                            <>
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Finishing Course... ({finishProgress.current}/
                              {finishProgress.total})
                            </>
                          ) : (
                            <>
                              <i className="isax isax-tick-circle me-2" />
                              Finish Course
                            </>
                          )}
                        </button>
                        {isFinishingCourse && (
                          <div className="mt-3">
                            <div className="progress" style={{ height: '6px' }}>
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{
                                  width: `${
                                    finishProgress.total > 0
                                      ? (finishProgress.current /
                                          finishProgress.total) *
                                        100
                                      : 0
                                  }%`,
                                }}
                                aria-valuenow={finishProgress.current}
                                aria-valuemin={0}
                                aria-valuemax={finishProgress.total}
                              />
                            </div>
                            <small className="text-muted mt-1 d-block">
                              Processing lessons... {finishProgress.current} of{' '}
                              {finishProgress.total} completed
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Course Completed Message */}
                  {isCourseCompleted() && (
                    <div className="mt-4 p-3 border rounded bg-success bg-opacity-10">
                      <div className="text-center">
                        <i className="isax isax-tick-circle text-success fs-1 mb-2" />
                        <h6 className="text-success mb-2">Course Completed!</h6>
                        <p className="text-muted small mb-0">
                          Congratulations! You have completed all lessons in
                          this course.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-lg-8 col-12">
                <div className="course-watch-content">
                  {/* Title, Label, and Duration above the player */}
                  <div className="mb-3">
                    <h4 className="mb-1 fw-bold fs-18 fs-md-22">
                      {selectedLesson?.name || course.title}
                    </h4>
                    <div className="d-flex align-items-center">
                      <small className="text-muted fs-13">
                        {selectedLesson?.description || ''}
                      </small>
                    </div>
                  </div>
                  <div className="video-player-container position-relative w-100 overflow-hidden rounded-4 shadow-sm" style={{ aspectRatio: '16/9', backgroundColor: '#000' }}>
                    {videoUrl ? (
                      <ReactPlayer
                        url={videoUrl}
                        controls={true}
                        key={videoUrl}
                        width="100%"
                        height="100%"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                        }}
                        config={{
                          youtube: {
                            playerVars: {
                              modestbranding: 1,
                              rel: 0,
                            },
                          },
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center text-muted p-4 text-center h-100 w-100"
                        style={{
                          backgroundColor: '#f8f9fa',
                          border: '2px dashed #dee2e6',
                          borderRadius: '16px',
                        }}
                      >
                        <i
                          className="isax isax-video-play text-muted"
                          style={{ fontSize: '48px', marginBottom: '16px' }}
                        />
                        <h5 className="text-muted mb-2 fw-semibold">
                          Video Tidak Tersedia
                        </h5>
                        <p className="text-muted mb-0 fs-13">
                          Video untuk lesson ini belum tersedia
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Prev / Next Controls */}
                  {course?.topics && (
                    <div
                      className="d-flex flex-row align-items-center justify-content-between gap-3 flex-wrap mt-4 mt-md-5"
                    >
                      <button
                        className="btn btn-outline-secondary"
                        disabled={!getPrevLesson()}
                        onClick={() => {
                          const prev = getPrevLesson()
                          if (prev) setSelectedLesson(prev)
                        }}
                      >
                        <i className="isax isax-arrow-left me-2" /> Prev
                      </button>
                      <button
                        className="btn btn-primary"
                        disabled={!getNextLesson()}
                        onClick={() => {
                          const next = getNextLesson()
                          if (next) setSelectedLesson(next)
                        }}
                      >
                        Next <i className="isax isax-arrow-right ms-2" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Certificate Download Modal */}
      {showCertificateModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Download Sertifikat</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCertificateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                    yang tertera pada formulir di bawah ini?
                  </p>
                  <div className="mb-3">
                    <label htmlFor="certificateName" className="form-label">
                      Nama pada Sertifikat
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="certificateName"
                      value={certificateName}
                      onChange={(e) => setCertificateName(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                  <div className="alert alert-warning">
                    <i className="isax isax-warning-2 me-2" />
                    Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                    di generate satu kali.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificateModal(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmCertificateDownload}
                    disabled={!certificateName.trim()}
                  >
                    Download Sertifikat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* MODAL JOIN GROUP */}
      {showGroupModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  <i className="isax isax-people text-info me-2" />
                  Pilih Group
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowGroupModal(false)}
                />
              </div>
              <div className="modal-body py-4">
                <p className="text-muted small mb-4">
                  Silakan pilih group WhatsApp / Telegram yang ingin Anda ikuti:
                </p>
                <div className="d-grid gap-3">
                  {course.link_group && (
                    <a
                      href={course.link_group}
                      target="_blank"
                      rel="noreferrer"
                      className="btn text-white fw-bold py-3 rounded-pill d-flex align-items-center justify-content-center shadow-sm"
                      style={{ backgroundColor: '#00bcd4', fontSize: '15px' }}
                    >
                      <i className="isax isax-people me-2 fs-18" />
                      Join Group Peserta
                    </a>
                  )}
                  {course.link_group_loker && (
                    <a
                      href={course.link_group_loker}
                      target="_blank"
                      rel="noreferrer"
                      className="btn text-white fw-bold py-3 rounded-pill d-flex align-items-center justify-content-center shadow-sm"
                      style={{ backgroundColor: '#0284c7', fontSize: '15px' }}
                    >
                      <i className="isax isax-briefcase me-2 fs-18" />
                      Join Group Lowongan Kerja
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FEEDBACK REQUIREMENT */}
      {showFeedbackModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1065 }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  <i className="isax isax-message-edit text-warning me-2" />
                  Feedback Peserta Diperlukan
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFeedbackModal(false)}
                />
              </div>
              <div className="modal-body py-4 text-center">
                <div
                  className="mb-3 d-inline-flex align-items-center justify-content-center rounded-circle bg-light-warning"
                  style={{ width: 64, height: 64 }}
                >
                  <i className="isax isax-warning-2 text-warning fs-32" />
                </div>
                <h6 className="fw-semibold mb-2">Isi Feedback Peserta Terlebih Dahulu</h6>
                <p className="text-muted small mb-4 px-3">
                  Button download sertifikat hanya bisa dibuka ketika Anda sudah mengisi feedback peserta. Silakan klik tombol di bawah untuk mengisi form masukan.
                </p>
                <div className="d-grid gap-2">
                  <button
                    type="button"
                    className="btn text-white fw-bold py-3 rounded-pill d-flex align-items-center justify-content-center shadow-sm"
                    style={{ backgroundColor: '#4361ee', fontSize: '15px' }}
                    onClick={() => {
                      setShowFeedbackModal(false)
                      handleFeedbackClick()
                    }}
                  >
                    <i className="isax isax-message-edit me-2 fs-18" />
                    Isi Feedback Peserta Sekarang
                  </button>
                  <button
                    type="button"
                    className="btn btn-light rounded-pill py-2 text-muted fw-semibold"
                    onClick={() => setShowFeedbackModal(false)}
                  >
                    Nanti Saja
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CourseContent
